/**
 * OTRACK-SUBSYSTEM (local Codex review round 6 P2 — "Cancel dwell timers
 * when the draft prompt changes") — extracted from `Scene3DStage.tsx` (was
 * over its 2000-line cap — CLAUDE.md "Контроль размера файлов") purely to
 * keep that file under the limit; pure code move, no behaviour change.
 *
 * "Draft generation" counter: bumped whenever the underlying draft
 * session's COMMAND or its committed POINT COUNT changes. Mirrors
 * `App.tsx`'s own `commandChanged || pointCountChanged`
 * `osnapExtendedOverride`-invalidation triggers — round-9/round-10 P2
 * hardened that EXACT signal for the EXACT same "stale armed state must not
 * cross a command-instance boundary" bug class:
 *
 *   - a command can hot-swap straight into a DIFFERENT command's
 *     SAME-shaped point step with no intervening `null`/non-accepting
 *     state (`commandChanged`);
 *   - a point can be committed via TYPED command-line / Dynamic-Input text
 *     with no pointer move (`pointCountChanged`).
 *
 * Neither transition is distinguishable from "the same in-flight prompt" by
 * `StageInteractionBindings.draftPointPromptActiveRef` alone (a plain
 * boolean) — a pending OTRACK hover-dwell timer armed just before either
 * transition, with no intervening pointer move, would otherwise validate
 * against a NEW, unrelated command's prompt at fire time and silently
 * acquire the OLD command's stale snap candidate into it.
 *
 * Local Codex review round 8 P2 ("Invalidate dwell timers across TT prompt
 * changes") — ALSO bumps whenever `draftSessionAcceptsDwellAcquisition`'s
 * OWN boolean result changes, even with `command`/`points.length`
 * unchanged. A `TT` sub-prompt (or a point filter, or an entity-pick step)
 * flips that boolean to `false` and back to `true` WITHOUT touching command
 * identity or point count — a dwell timer armed just before `TT` starts,
 * whose fire falls entirely inside the brief TT sub-prompt window (typed
 * `TT` + a same-position click, both with no intervening pointer move),
 * would otherwise see `draftPointPromptActiveRef.current === true` again by
 * fire time (TT already resumed the host prompt) and silently toggle the
 * SAME point TT itself just acquired, un-acquiring it. Bumping generation
 * on this transition too closes that gap through the SAME fire-time
 * `armedForGeneration` check, without touching the narrower
 * `useOtrackAcquisitionEffects` command-only clear (round 7 P2) — that
 * effect governs the ACQUIRED-POINT STORE and must stay per-command
 * (acquired points persist across multiple points of the same command);
 * this ref only governs the DWELL TIMER's own validity, where being more
 * conservative just means "the timer needs to re-arm", never a lost
 * feature.
 *
 * Mutated DURING RENDER (like `Scene3DStage.tsx`'s own `draftActiveRef`
 * pattern) so the returned ref's `.current` is always current for the SAME
 * render that produced it — a stable ref object, read at event-time by the
 * once-installed pick-loop handlers, never a `useEffect` dependency.
 */

import { useRef, type MutableRefObject } from "react";

import type { DraftSession } from "../../scene/draftingHelpers";
import { draftSessionAcceptsDwellAcquisition } from "../../scene/otrackTrackPointSession";

/**
 * Returns a stable ref whose `.current` increments whenever `draftSession`'s
 * `command`, `points.length`, or `draftSessionAcceptsDwellAcquisition`
 * result differs from the previous call (a `null` session counts as
 * `command: null, points.length: 0, acceptsDwell: false`).
 */
export function useDraftGenerationRef(
  draftSession: DraftSession | null | undefined,
): MutableRefObject<number> {
  const commandRef = useRef(draftSession?.command ?? null);
  const pointCountRef = useRef(draftSession?.points.length ?? 0);
  const acceptsDwellRef = useRef(draftSessionAcceptsDwellAcquisition(draftSession ?? null));
  const generationRef = useRef(0);

  const currentCommand = draftSession?.command ?? null;
  const currentPointCount = draftSession?.points.length ?? 0;
  const currentAcceptsDwell = draftSessionAcceptsDwellAcquisition(draftSession ?? null);
  if (
    currentCommand !== commandRef.current ||
    currentPointCount !== pointCountRef.current ||
    currentAcceptsDwell !== acceptsDwellRef.current
  ) {
    generationRef.current += 1;
    commandRef.current = currentCommand;
    pointCountRef.current = currentPointCount;
    acceptsDwellRef.current = currentAcceptsDwell;
  }

  return generationRef;
}
